import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import type { AIUsageInfo } from '../services/aiService';
import { trackEvent } from '../lib/usageTracker';
import { fmtGT, todayGT } from '../utils/gt-date';

export interface AIQuota {
  hasSubscription: boolean;
  used: number;
  limit: number;
  percentage: number;
  remaining: number;
  periodStart: string | null;
  periodEnd: string | null;
  // Term-purchase end date ("paid through"). Only present while the paid term
  // is active; null for comp grants and once the term lapses (status='expired').
  paidThrough: string | null;
  status: 'active' | 'cancelled' | 'expired' | 'none';
  /** Free 14-day trial (paidThrough = its last moment). */
  isTrial: boolean;
  /** Can start the free trial now (never had Aura, eligible role, daily slots left). */
  trialEligible: boolean;
  /** Their trial ran out and nothing replaced it. */
  trialEnded: boolean;
  /** Today's (Guatemala) usage and cap. Internal only — the UI never shows token numbers. */
  dailyUsed: number;
  dailyCap: number;
}

const EMPTY_QUOTA: AIQuota = {
  hasSubscription: false,
  used: 0,
  limit: 0,
  percentage: 0,
  remaining: 0,
  periodStart: null,
  periodEnd: null,
  paidThrough: null,
  status: 'none',
  isTrial: false,
  trialEligible: false,
  trialEnded: false,
  dailyUsed: 0,
  dailyCap: 0,
};

/** Guatemala calendar days left in a trial ending at `paidThrough` (0 = ends today). */
export function trialDaysLeft(paidThrough: string | null | undefined): number | null {
  const end = paidThrough ? fmtGT(paidThrough).slice(0, 10) : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return null;
  return Math.max(0, Math.round((Date.parse(end) - Date.parse(todayGT())) / 86_400_000));
}

/** "Prueba · 5 días" / "Prueba · 1 día" / "Prueba · último día". */
export function trialBadge(paidThrough: string | null | undefined): string {
  const n = trialDaysLeft(paidThrough);
  if (n == null) return 'Prueba';
  return n === 0 ? 'Prueba · último día' : `Prueba · ${n} ${n === 1 ? 'día' : 'días'}`;
}

export function useAIQuota() {
  const [quota, setQuota] = useState<AIQuota>(EMPTY_QUOTA);
  const [loading, setLoading] = useState(true);
  // A failed refresh keeps the last good quota (a network blip must not flip a
  // paying user to the upsell) and raises `error` so the UI can offer a retry.
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('get_ai_quota');
      if (error || !data) {
        setError(true);
        return;
      }

      setError(false);
      setQuota({
        hasSubscription: data.has_subscription ?? false,
        used: data.used ?? 0,
        limit: data.limit ?? 0,
        percentage: data.percentage ?? 0,
        remaining: data.remaining ?? 0,
        periodStart: data.period_start ?? null,
        periodEnd: data.period_end ?? null,
        paidThrough: data.paid_through ?? null,
        status: data.status ?? 'none',
        isTrial: data.is_trial === true,
        trialEligible: data.trial_eligible === true,
        trialEnded: data.trial_ended === true,
        dailyUsed: data.daily_used ?? 0,
        dailyCap: data.daily_cap ?? 0,
      });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  /** Apply the usage a chat turn reported (stream `done.usage`) without
   *  re-querying the RPC. Same rounding as get_ai_quota (1 decimal). */
  const applyUsage = useCallback((usage: AIUsageInfo) => {
    setQuota(q => {
      if (!q.hasSubscription || !(usage.period_limit > 0)) return q;
      const used = usage.period_total;
      const limit = usage.period_limit;
      return {
        ...q,
        used,
        limit,
        percentage: Math.round((used / limit) * 1000) / 10,
        remaining: Math.max(limit - used, 0),
      };
    });
  }, []);

  /** Starts the free trial (start_ai_trial). Resolves to null on success, else
   *  the server's Spanish reason. Re-reads the quota either way. */
  const startTrial = useCallback(async (): Promise<string | null> => {
    if (!supabase) return 'No se pudo iniciar la prueba. Intenta de nuevo.';
    const { error } = await supabase.rpc('start_ai_trial');
    await refresh();
    if (error) return error.message || 'No se pudo iniciar la prueba. Intenta de nuevo.';
    trackEvent('ai_trial_start');
    return null;
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return useMemo(() => ({ ...quota, loading, error, refresh, applyUsage, startTrial }), [quota, loading, error, refresh, applyUsage, startTrial]);
}
